import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

interface LegalDocument {
  id: string;
  title: string;
  filename: string;
  content: string;
}

export const TermsAndConditions: React.FC = () => {
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDocuments = async () => {
      try {
        const documentFiles = [
          { id: 'terms-of-service', title: 'Terms of Service', filename: 'terms-of-service.md' },
          { id: 'privacy-policy', title: 'Privacy Policy', filename: 'privacy-policy.md' },
          { id: 'cookie-policy', title: 'Cookie Policy', filename: 'cookie-policy.md' },
          { id: 'data-processing', title: 'Data Processing Agreement', filename: 'data-processing.md' }
        ];

        const loadedDocuments: LegalDocument[] = [];

        for (const doc of documentFiles) {
          try {
            const response = await fetch(`/docs/legal/${doc.filename}`);
            if (response.ok) {
              const content = await response.text();
              loadedDocuments.push({
                ...doc,
                content
              });
            }
          } catch (err) {
            console.warn(`Failed to load ${doc.filename}:`, err);
          }
        }

        if (loadedDocuments.length === 0) {
          throw new Error('Failed to load any legal documents');
        }

        setDocuments(loadedDocuments);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading) {
    return (
      <div className="terms-page">
        <Header />
        <main className="main-content">
          <div className="container">
            <div className="terms-content">
              <div className="loading">Loading terms and conditions...</div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="terms-page">
        <Header />
        <main className="main-content">
          <div className="container">
            <div className="terms-content">
              <div className="error">Error loading terms and conditions: {error}</div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="terms-page">
      <Header />
      <main className="main-content">
        <div className="container">
          <div className="terms-content">
            {/* Table of Contents */}
            <div className="toc-container">
              <h1>Terms & Conditions</h1>
              <p className="toc-description">
                All legal documents and policies for Good Old Maps. Click on any section below to jump directly to that document.
              </p>
              <nav className="toc-nav">
                {documents.map((doc) => (
                  <button
                    key={doc.id}
                    className="toc-link"
                    onClick={() => scrollToSection(doc.id)}
                  >
                    {doc.title}
                  </button>
                ))}
              </nav>
            </div>

            {/* Legal Documents */}
            {documents.map((doc) => (
              <section key={doc.id} id={doc.id} className="legal-document">
                <div className="markdown-content">
                  <ReactMarkdown>
                    {doc.content}
                  </ReactMarkdown>
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
